import { Channel, IdentifiedUser, IntegrationRequest, User } from 'app/core';
import { Motive } from './motive.model';

import { StreamEvent } from './stream-event.model';
import { Tag } from './tag.model';

export type TicketState =
    | 'active'
    | 'closed'
    | 'unassigned'
    | 'preClosed'
    | 'preAssigned'
    | 'pending'
    | 'onHold'
    | 'unassignedOutbound'
    | 'preUnassignedOutbound'
    | 'assignedOutbound'
    | 'callingOutbound';

export type TicketMode = 'automatic' | 'manual';
export type TicketCallStatus = 'onHold' | 'onGoing' | 'finished';
export type ReleaseType = 'transfer' | 'forward' | 'normal';

export class Ticket {
    id: string;
    index: string;
    channel: Channel;
    state: TicketState;
    mode: TicketMode;
    createdAt: number;
    updatedAt: number;
    escalatedAt: number;
    controlTakenAt: number;
    preAssignedAt: number;

    systemTags: Tag[];
    operatorsTags: Tag[];

    motive: Motive;

    customer: {
        id: string;
        name: string;
    };

    operator: User;
    streamId: string;
    firstStreamEventId: string;
    lastStreamEventId: string;
    latestStreamEvent: StreamEvent;
    latestChatEvent: StreamEvent;

    controlHistory: {
        operator: User;
        takenOn: Date;
        releasedOn: Date;
        releaseType: ReleaseType;
    }[];

    totalUnreadMessages: number;

    lastCallDuration: number = null;
    chatSessionId: string; // property only exists on Widget Channel

    isAnswered: boolean = false;
    failedOutboundAttempts: number = 0;
    lastCallEndedAt: number;
    lastCallStartedAt: number;
    lastOutboundAttemptAt: number;

    language: string;

    preferredLanguages: string[];

    get conversationLanguage(): string {
        return this.language ?? (this.preferredLanguages ? this.preferredLanguages[0] : null);
    }

    identifiedUsers?: IdentifiedUser[];
    clientConnectionId?: string;

    integrationRequests?: IntegrationRequest[];

    constructor(
        id: string,
        index: string,
        channel: Channel,
        state: TicketState,
        mode: TicketMode,
        createdAt: number,
        updatedAt: number,
        escalatedAt: number,
        controlTakenAt: number,
        preAssignedAt: number,
        systemTags: Tag[],
        operatorsTags: Tag[],
        customer: { id: string; name: string },
        operator: User,
        streamId: string,
        firstStreamEventId: string,
        lastStreamEventId: string,
        latestStreamEvent: StreamEvent,
        latestChatEvent: StreamEvent,
        controlHistory: { operator: User; takenOn: Date; releasedOn: Date; releaseType: ReleaseType }[],
        totalUnreadMessages: number,
        lastCallDuration?: number,
        chatSessionId?: string,
        isAnswered?: boolean,
        failedOutboundAttempts?: number,
        lastCallEndedAt?: number,
        motive?: Motive,
        lastCallStartedAt?: number,
        lastOutboundAttemptAt?: number,
        identifiedUsers?: IdentifiedUser[],
        clientConnectionId?: string,
        integrationRequests?: IntegrationRequest[],
    ) {
        this.id = id;
        this.index = index;
        this.channel = channel;
        this.state = state;
        this.mode = mode;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.escalatedAt = escalatedAt;
        this.controlTakenAt = controlTakenAt;
        this.preAssignedAt = preAssignedAt;
        this.systemTags = systemTags;
        this.operatorsTags = operatorsTags;
        this.customer = customer;
        this.operator = operator;
        this.streamId = streamId;
        this.firstStreamEventId = firstStreamEventId;
        this.lastStreamEventId = lastStreamEventId;
        this.latestStreamEvent = latestStreamEvent;
        this.latestChatEvent = latestChatEvent;
        this.controlHistory = controlHistory;
        this.totalUnreadMessages = totalUnreadMessages;
        this.lastCallDuration = lastCallDuration ?? 0;
        this.chatSessionId = chatSessionId;
        this.isAnswered = isAnswered;
        this.failedOutboundAttempts = failedOutboundAttempts ?? 0;
        this.lastCallEndedAt = lastCallEndedAt;
        this.motive = motive;
        this.lastCallStartedAt = lastCallStartedAt;
        this.lastOutboundAttemptAt = lastOutboundAttemptAt;
        this.identifiedUsers = identifiedUsers;
        this.clientConnectionId = clientConnectionId;
        this.integrationRequests = integrationRequests;
    }
}
